import React from "react";
import QmsModalPortal from "../qms/QmsModalPortal";

function TimelineCommChip({ channel }) {
  const slug = String(channel || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  return (
    <span className={`schedule-activity-ai-summary__comm-chip is-${slug}`}>
      {channel}
    </span>
  );
}

function AppointmentTimeline({ timeline }) {
  if (!timeline) return null;

  return (
    <div className="schedule-activity-ai-summary__timeline">
      <div className="schedule-activity-ai-summary__timeline-anchor">
        <i className="fas fa-calendar-day" aria-hidden />
        <div>
          <span className="schedule-activity-ai-summary__timeline-anchor-label">
            First appointment anchor
          </span>
          <strong>{timeline.anchorLabel}</strong>
        </div>
      </div>
      {timeline.visits?.length ? (
        <ol className="schedule-activity-ai-summary__timeline-list">
          {timeline.visits.map((entry, index) => (
          <li
            key={`${entry.visitId || entry.visitName}-${index}`}
            className={`schedule-activity-ai-summary__timeline-item${
              entry.isAdhoc ? " is-adhoc" : ""
            }`}
          >
            <div className="schedule-activity-ai-summary__timeline-visit">
              <strong>{entry.visitName}</strong>
              <span>{entry.appointmentLabel}</span>
              {entry.dayOffset !== 0 ? (
                <span className="schedule-activity-ai-summary__timeline-offset">
                  Day {entry.dayOffset >= 0 ? `+${entry.dayOffset}` : entry.dayOffset}
                </span>
              ) : null}
            </div>
            {entry.communications?.length ? (
              <div className="schedule-activity-ai-summary__timeline-comms">
                {entry.communications.map((comm) => (
                  <div
                    key={`${entry.visitName}-${comm.type}`}
                    className="schedule-activity-ai-summary__timeline-comm"
                  >
                    <span className="schedule-activity-ai-summary__timeline-comm-label">
                      {comm.label}
                    </span>
                    <div className="schedule-activity-ai-summary__timeline-comm-chips">
                      {comm.channels.length ? (
                        comm.channels.map((channel) => (
                          <TimelineCommChip key={`${comm.type}-${channel}`} channel={channel} />
                        ))
                      ) : (
                        <span className="schedule-activity-ai-summary__comm-empty">None</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </li>
        ))}
        </ol>
      ) : (
        <p className="schedule-activity-ai-summary__note">
          No visits found on this schedule yet.
        </p>
      )}
    </div>
  );
}

function PendingImpactList({ items = [], hiddenCount = 0 }) {
  if (!items.length) return null;

  return (
    <>
      <p className="schedule-activity-ai-summary__gaps-intro">
        These pending items are still incomplete. If you leave them, this is what can go wrong.
      </p>
      <ol className="schedule-activity-ai-summary__priority-list">
        {items.map((item, index) => (
          <li
            key={`${item.scope}-${item.label}-${index}`}
            className={`schedule-activity-ai-summary__priority-item schedule-activity-ai-summary__priority-item--${item.status}${
              item.optionalForLive ? " is-optional" : ""
            }`}
          >
            <strong>
              {item.scope}
              {item.label && item.label !== item.scope ? ` · ${item.label}` : ""}
              {item.optionalForLive ? (
                <span className="schedule-activity-ai-summary__optional">Optional</span>
              ) : null}
            </strong>
            {item.message && item.message !== item.label ? <span>{item.message}</span> : null}
            {item.impact ? (
              <span className="schedule-activity-ai-summary__priority-impact">
                {item.impactPrefix || "If not completed"}: {item.impact}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      {hiddenCount > 0 ? (
        <p className="schedule-activity-ai-summary__more">
          +{hiddenCount} more item{hiddenCount === 1 ? "" : "s"} — see Go live checklist for
          full list.
        </p>
      ) : null}
    </>
  );
}

export default function ScheduleActivityAiSummaryModal({
  open,
  onClose,
  loading,
  error,
  summary,
  isScheduleLive = false,
  variant = "view",
  onContinue,
  continueLabel = "Continue to publish",
  continueBusy = false,
}) {
  if (!open) return null;

  const isPublish = variant === "publish";
  const isFlowSummary = summary?.summaryMode === "flow";
  const readiness = summary?.readiness;
  const timeline = summary?.appointmentTimeline;
  const progressPct =
    readiness?.total > 0
      ? Math.round((readiness.complete / readiness.total) * 100)
      : 0;
  const canContinue = Boolean(summary?.canGoLive) && !loading && !error;
  const showTimeline = isFlowSummary && Boolean(timeline);
  const showDraftSummary = !isFlowSummary;

  const title = isFlowSummary
    ? "How your appointments will flow"
    : isPublish
      ? summary?.canGoLive
        ? "Ready to publish"
        : "Complete setup before publishing"
      : summary?.canGoLive
        ? "Ready to go live"
        : "What you need to finish";

  const loadingMessage = isFlowSummary || isScheduleLive
    ? "Building appointment timeline…"
    : "Checking readiness…";

  return (
    <QmsModalPortal>
      <div
        className="schedule-activity-ai-summary__backdrop"
        onClick={isPublish ? undefined : onClose}
      />
      <div
        className={`schedule-activity-ai-summary__dialog schedule-activity-ai-summary__dialog--compact${
          isPublish ? " schedule-activity-ai-summary__dialog--publish" : ""
        }${showTimeline ? " schedule-activity-ai-summary__dialog--timeline" : ""}`}
        role="dialog"
        aria-labelledby="schedule-activity-ai-summary-title"
        aria-modal="true"
      >
        <div className="schedule-activity-ai-summary__head">
          <div>
            <div className="schedule-activity-ai-summary__eyebrow">
              <i className="fas fa-wand-magic-sparkles" aria-hidden />
              AI Summary
            </div>
            <h3 id="schedule-activity-ai-summary-title" className="schedule-activity-ai-summary__title">
              {title}
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link schedule-activity-ai-summary__close"
            onClick={onClose}
            aria-label="Close AI summary"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <div className="schedule-activity-ai-summary__body">
          {loading ? (
            <div className="schedule-activity-ai-summary__loading">
              <i className="fas fa-spinner fa-spin" aria-hidden />
              {loadingMessage}
            </div>
          ) : null}

          {!loading && error ? (
            <div className="schedule-activity-ai-summary__error">{error}</div>
          ) : null}

          {!loading && !error && summary ? (
            <>
              <p
                className={`schedule-activity-ai-summary__lead${
                  showTimeline || summary.canGoLive
                    ? " schedule-activity-ai-summary__lead--ready"
                    : " schedule-activity-ai-summary__lead--blocked"
                }`}
              >
                {summary.headline}
              </p>

              {showTimeline ? <AppointmentTimeline timeline={timeline} /> : null}

              {readiness ? (
                <div className="schedule-activity-ai-summary__stats">
                  <span>{progressPct}% ready</span>
                  <span>{readiness.pending} pending</span>
                  {readiness.blocked > 0 ? <span>{readiness.blocked} blocked</span> : null}
                </div>
              ) : null}

              {summary.reportError ? (
                <p className="schedule-activity-ai-summary__note">{summary.reportError}</p>
              ) : null}

              <PendingImpactList
                items={summary.priorityActions}
                hiddenCount={summary.hiddenCount}
              />

              {showDraftSummary && summary.canGoLive && !isPublish ? (
                <p className="schedule-activity-ai-summary__note">
                  All required checks are complete. Use Publish schedule when you are ready.
                </p>
              ) : null}

              {showDraftSummary && summary.latestChange && !isPublish ? (
                <p className="schedule-activity-ai-summary__footnote">
                  Last change: {summary.latestChange}
                </p>
              ) : null}

              {isPublish ? (
                <p className="schedule-activity-ai-summary__publish-warning">
                  This action can&apos;t be undone.
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        {isPublish ? (
          <div className="schedule-activity-ai-summary__footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
              disabled={continueBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`btn btn-sm ${summary?.isScheduleLive ? "btn-primary" : "btn-success"}`}
              onClick={onContinue}
              disabled={!canContinue || continueBusy}
              title={
                canContinue
                  ? continueLabel
                  : "Complete all required checks (100% readiness) before publishing."
              }
            >
              {continueBusy ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  <span>Publishing…</span>
                </>
              ) : (
                continueLabel
              )}
            </button>
          </div>
        ) : null}
      </div>
    </QmsModalPortal>
  );
}
